import React, { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
const buttonVariants = cva('inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-normal ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 motion-safe:active:scale-[0.98] motion-reduce:transform-none', {
  variants: {
    variant: {
      default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:shadow-glow',
      cta: "relative isolate overflow-hidden bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:shadow-glow before:pointer-events-none before:absolute before:inset-y-0 before:left-[-40%] before:w-1/3 before:-skew-x-12 before:bg-white/25 before:opacity-0 before:transition-all before:duration-500 before:ease-standard before:content-[''] motion-safe:hover:before:left-[120%] motion-safe:hover:before:opacity-100 motion-reduce:before:hidden",
      destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
      outline: 'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
      secondary: 'border border-transparent bg-secondary text-secondary-foreground shadow-sm hover:border-border/80 hover:bg-secondary/80 hover:shadow-md',
      ghost: 'hover:bg-accent hover:text-accent-foreground',
      link: 'text-primary underline-offset-4 hover:underline'
    },
    size: {
      default: 'h-10 px-4 py-2',
      sm: 'h-9 rounded-md px-3',
      lg: 'h-11 rounded-md px-8',
      icon: 'h-10 w-10'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
}
const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  loadingText,
  children,
  disabled,
  ...props
}, ref) => {
  const classes = cn(buttonVariants({
    variant,
    size,
    className
  }));
  if (asChild) {
    return <Slot className={classes} ref={ref} aria-busy={loading ? true : undefined} {...props}>
        {loading ? <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            <span>{loadingText ?? 'Wird geladen...'}</span>
          </> : children}
      </Slot>;
  }
  return <button className={classes} ref={ref} disabled={disabled || loading} aria-busy={loading ? true : undefined} {...props}>
      {loading ? <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          <span>{loadingText ?? 'Wird geladen...'}</span>
        </> : children}
    </button>;
});
Button.displayName = 'Button';
export { Button, buttonVariants };